/**
 * Charting on system entry (spec §6.2, plan R18): visiting a system charts it (R5's `markCharted`, which also rumours
 * the far end of every relay touching it), with `REV.systemCharted` / `REV.systemRumoured` for each change. The first
 * visit accrues the system's chart value (`base × novelty × first discovery × minerals factor`, MC-a8, plus the anchor
 * bonus once), recording a first discovery; every non-hidden POI is discovered with its richness. Rare (system entry):
 * allocates freely.
 */
import type { SystemLayout } from '../galaxy/layout/types'
import { emitReach, REV } from '../reach/events'
import type { ReachCore } from '../reach/runtime/types'
import { markCharted, markFirstDiscovery } from '../reach/state'
import { galaxyIndex } from '../reach/state/galaxyIndex'
import type { ReachState } from '../reach/types'
import { accrue, revealPoi, type ExplorationContext } from './context'
import { mineralsKnowledge } from './minerals'
import { anchorChartValue, chartMineralFactor, chartMultipliers, isFirstDiscovery, systemChartValue } from './valuation'

/**
 * True once the player's own visit has put value on the books for the system (unsold, sold or first discovery), or a
 * bought Holdfast survey charted it (MC-a10): a surveyed system's first visit pays only POI richness, never the system
 * or anchor value, so buying a survey can't be turned into chart income.
 */
export function systemValued(state: ReachState, systemId: string): boolean {
  const charts = state.charts
  return (
    (charts.unsold[systemId] ?? 0) > 0 ||
    (charts.soldTotal[systemId] ?? 0) > 0 ||
    charts.firstDiscoveries.includes(systemId) ||
    (charts.surveyed?.includes(systemId) ?? false)
  )
}

/** Emits `REV.systemRumoured` for each system id (by galaxy index). */
export function emitRumoured(core: ReachCore, ids: readonly string[]): void {
  const systems = galaxyIndex(core.galaxy).systems
  for (const id of ids) emitReach(core.events, REV.systemRumoured, systems.get(id)?.index ?? -1, 0, 0, 0, 0, 0)
}

export function chartSystem(x: ExplorationContext, core: ReachCore, layout: SystemLayout): void {
  const state = core.state
  const id = layout.systemId
  chartMultipliers(x.initial, id, x.cfg, x.mult)
  const valued = systemValued(state, id)
  const result = markCharted(state, core.galaxy, id)
  // A surveyed system (MC-a10) was charted without a visit and without `REV.systemCharted` (that event readies chart
  // contracts). Its first visit sends it, and the empty POI list marks the visit made.
  const surveyed = state.charts.surveyed?.includes(id) ?? false
  const surveyedFirstVisit = surveyed && state.discoveredPois[id] === undefined
  if (surveyed) state.discoveredPois[id] ??= []
  if (result.charted || surveyedFirstVisit) emitReach(core.events, REV.systemCharted, layout.systemIndex, 0, 0, 0, 0, 0)
  emitRumoured(core, result.rumoured)
  if (!valued) {
    if (isFirstDiscovery(x.initial, id)) markFirstDiscovery(state, id)
    // The signature and grades the visit just charted (MC-a7a) set the minerals factor (MC-a8).
    const mineral = chartMineralFactor(mineralsKnowledge(state, core.galaxy, id), x.cfg)
    let value = systemChartValue(layout.region, x.mult, x.cfg, mineral)
    if (layout.anchor !== null) value += anchorChartValue(layout.region, x.mult, x.cfg)
    accrue(core, layout, value)
  }
  for (const poi of layout.pois) if (!poi.hidden) revealPoi(x, core, layout, poi)
}
